import React, { memo } from 'react';
import Icon from '../components/Icon';
import Badge from '../components/Badge';

// ========== mp-lobby: Creator 大厅 / Joiner 等待 ==========
const MpLobbySection = memo(({
  activePlayers, playerHandicaps,
  mp, showToast,
  setCurrentSection,
  startSoloGame,
  t
}) => {
  const joinUrl = `https://handincap.golf?join=${mp.gameCode}`;
  const hasJoiners = mp.playerDeviceCount > 0 || mp.viewerDeviceCount > 0;

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-orange-50 py-6">
              <div className="max-w-md mx-auto px-4 space-y-4">
                <div className="text-center">
                  <h2 className="text-xl font-bold text-gray-900">
                    {mp.multiplayerRole === 'creator' ? <><Icon name="sync" size={14} className="inline-block mr-1 align-text-bottom" />{t('mpWaitingPartner')}</> : <><Icon name="sync" size={14} className="inline-block mr-1 align-text-bottom" />{t('mpWaitingStart')}</>}
                  </h2>
                </div>

                {/* Creator: Game Code + QR Code + Share Link */}
                {mp.multiplayerRole === 'creator' && (
                <div className="bg-white rounded-xl p-6 shadow-md text-center">
                  <p className="text-sm text-gray-500 mb-2">
                    {t('mpRoomCode')}
                  </p>
                  <div className="text-4xl font-mono font-bold tracking-[0.3em] text-amber-600 bg-amber-50 rounded-lg py-3">
                    {mp.gameCode}
                  </div>

                  {/* Share / Copy buttons */}
                  <div className="flex gap-2 mt-3 justify-center">
                    <button
                      onClick={() => {
                        navigator.clipboard?.writeText(joinUrl);
                        showToast(t('mpLinkCopied'), 'success');
                      }}
                      className="flex items-center gap-1.5 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition"
                    >
                      <Icon name="copy" size={16} />
                      {t('mpCopyLink')}
                    </button>
                    {navigator.share && (
                      <button
                        onClick={() => {
                          navigator.share({ url: joinUrl }).catch(() => {});
                        }}
                        className="flex items-center gap-1.5 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition"
                      >
                        <Icon name="share" size={16} />
                        {t('mpShareLink')}
                      </button>
                    )}
                  </div>

                  {/* QR Code */}
                  <div className="mt-3">
                    <img
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=160x160&data=${encodeURIComponent(joinUrl)}`}
                      alt="QR Code"
                      className="mx-auto w-40 h-40 rounded-lg"
                    />
                    <p className="text-xs text-gray-400 mt-1">{t('mpScanToJoin')}</p>
                  </div>
                </div>
                )}

                {/* Joiner/Viewer: Game Info */}
                {(mp.multiplayerRole === 'joiner' || mp.multiplayerRole === 'viewer') && mp.remoteGame && (
                <div className="bg-white rounded-xl p-4 shadow-md">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">
                    {t('mpGameInfo')}
                  </h3>
                  {mp.remoteGame.course?.fullName && (
                    <div className="flex items-center justify-between py-2 border-b">
                      <span className="text-sm text-gray-500">{t('mpCourse')}</span>
                      <span className="font-medium text-sm">{mp.remoteGame.course.fullName}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between py-2 border-b">
                    <span className="text-sm text-gray-500">{t('mpMode')}</span>
                    <span className="font-medium text-sm">
                      {mp.remoteGame.gameMode === 'matchPlay' ? <><Icon name="trophy" size={16} className="inline-block mr-1 align-text-bottom" />{t('matchPlay')}</> :
                       mp.remoteGame.gameMode === 'win123' ? <><Icon name="cash" size={16} className="inline-block mr-1 align-text-bottom" />{t('win123')}</> :
                       mp.remoteGame.gameMode === 'skins' ? <><Icon name="money-bag" size={16} className="inline-block mr-1 align-text-bottom" />{t('skins')}</> :
                       <><Icon name="suit-spade" size={16} className="inline-block mr-1 align-text-bottom" />{t('baccarat')}</>}
                    </span>
                  </div>
                  {mp.remoteGame.stake > 0 && (
                    <div className="flex items-center justify-between py-2 border-b">
                      <span className="text-sm text-gray-500">{t('mpStake')}</span>
                      <span className="font-medium text-sm text-green-600">RM {mp.remoteGame.stake}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between py-2 border-b">
                    <span className="text-sm text-gray-500">{t('mpRoom')}</span>
                    <span className="font-mono text-sm text-amber-600">{mp.gameCode}</span>
                  </div>
                  {mp.remoteGame.holesList && (
                    <div className="flex items-center justify-between py-2">
                      <span className="text-sm text-gray-500">{t('mpHolesLabel')}</span>
                      <span className="font-medium text-sm">
                        {mp.remoteGame.holesList.length === 18 ? t('f18') :
                         mp.remoteGame.holesList[0] === 1 && mp.remoteGame.holesList.length === 9 ? t('front9') :
                         mp.remoteGame.holesList[0] === 10 && mp.remoteGame.holesList.length === 9 ? t('back9') :
                         `${mp.remoteGame.holesList.length} (${mp.remoteGame.holesList[0]}-${mp.remoteGame.holesList[mp.remoteGame.holesList.length - 1]})`}
                      </span>
                    </div>
                  )}
                </div>
                )}

                {/* Player Claim Status */}
                <div className="bg-white rounded-xl p-4 shadow-md">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">
                    {t('mpPlayerAssignment')}
                  </h3>
                  {activePlayers.map(player => (
                    <div key={player} className="flex items-center justify-between py-2 border-b last:border-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{player}</span>
                        {playerHandicaps[player] > 0 && (
                          <span className="text-xs text-gray-400">HCP {playerHandicaps[player]}</span>
                        )}
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        mp.claimed[player]
                          ? mp.getDeviceBgClass(mp.claimed[player])
                          : 'bg-gray-100 text-gray-500'
                      }`}>
                        {mp.claimed[player]
                          ? (
                            <span className="inline-flex items-center gap-1">
                              <Badge {...mp.getDeviceBadge(mp.claimed[player])} size={18} />
                              {mp.claimed[player] === mp.deviceId && t('mpMine')}
                            </span>
                          )
                          : (t('mpUnclaimed'))}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Sync status indicator */}
                <div className="text-center">
                  <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm ${
                    mp.syncStatus === 'connected' ? 'bg-green-100 text-green-700' :
                    mp.syncStatus === 'error' ? 'bg-red-100 text-red-700' :
                    'bg-gray-100 text-gray-500'
                  }`}>
                    <span className={`w-2 h-2 rounded-full ${
                      mp.syncStatus === 'connected' ? 'bg-green-500 animate-pulse' :
                      mp.syncStatus === 'error' ? 'bg-red-500' :
                      'bg-gray-400'
                    }`}></span>
                    {mp.syncStatus === 'connected' ? t('mpConnected') :
                     mp.syncStatus === 'error' ? t('mpError') :
                     t('mpConnecting')}
                  </div>
                </div>

                {/* Creator: Connected devices summary */}
                {mp.multiplayerRole === 'creator' && (
                  <div className="bg-white rounded-xl p-4 shadow-md">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">
                      {t('mpConnectionStatus')}
                    </h3>
                    <div className="flex items-center justify-between py-2 border-b">
                      <div className="flex items-center gap-2">
                        <Icon name="target" size={20} />
                        <span className="text-sm text-gray-600">{t('mpPlayersJoined')}</span>
                      </div>
                      <span className={`text-sm font-bold px-2.5 py-0.5 rounded-full ${
                        mp.playerDeviceCount > 0 ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-400'
                      }`}>
                        {mp.playerDeviceCount}
                      </span>
                    </div>
                    <div className="flex items-center justify-between py-2">
                      <div className="flex items-center gap-2">
                        <Icon name="eye" size={20} />
                        <span className="text-sm text-gray-600">{t('mpViewersJoined')}</span>
                      </div>
                      <span className={`text-sm font-bold px-2.5 py-0.5 rounded-full ${
                        mp.viewerDeviceCount > 0 ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-400'
                      }`}>
                        {mp.viewerDeviceCount}
                      </span>
                    </div>
                    {mp.playerDeviceCount === 0 && (
                      <p className="text-xs text-amber-600 mt-2 bg-amber-50 rounded-lg px-3 py-2">
                        <Icon name="tip" size={14} className="inline-block mr-1 align-text-bottom" />{t('mpNoPlayersYet')}
                      </p>
                    )}
                  </div>
                )}

                {/* Creator: Start Game button — solo if no joiners, multiplayer if anyone joined */}
                {mp.multiplayerRole === 'creator' && (
                  <button
                    onClick={async () => {
                      if (hasJoiners) {
                        // Multiplayer mode: someone joined
                        mp.setMultiplayerOn(true);
                        const result = await mp.startMultiplayerGame();
                        if (result.ok) {
                          setCurrentSection('game');
                          mp.setMultiplayerSection(null);
                        }
                      } else {
                        // Solo mode: no one joined
                        mp.resetMultiplayer();
                        startSoloGame();
                      }
                    }}
                    className={`w-full py-3 px-4 rounded-lg font-semibold text-lg transition ${
                      hasJoiners
                        ? 'bg-green-600 hover:bg-green-700 text-white'
                        : 'bg-blue-600 hover:bg-blue-700 text-white'
                    }`}
                  >
                    {hasJoiners ? <><Icon name="golfer" size={16} className="inline-block mr-1 align-text-bottom" />{t('mpStartGame')}</> : t('startSolo')}
                  </button>
                )}

                {/* Joiner/Viewer: Waiting message */}
                {(mp.multiplayerRole === 'joiner' || mp.multiplayerRole === 'viewer') && (
                  <div className="text-center">
                    {mp.multiplayerRole === 'viewer' && (
                      <div className="mb-2 inline-flex items-center gap-2 bg-purple-100 text-purple-700 px-3 py-1.5 rounded-full text-sm font-medium">
                        <Icon name="eye" size={16} /> {t('mpViewerRole')}
                      </div>
                    )}
                    <p className="text-sm text-gray-500">
                      <Icon name="loading" size={14} className="inline-block mr-1 align-text-bottom" />{t('mpWaitingCreator')}
                    </p>
                  </div>
                )}

                {/* Back button */}
                <button
                  onClick={() => {
                    const wasJoiner = mp.multiplayerRole === 'joiner' || mp.multiplayerRole === 'viewer';
                    mp.resetMultiplayer();
                    mp.setMultiplayerSection(null);
                    setCurrentSection(wasJoiner ? 'home' : 'players');
                  }}
                  className="w-full bg-gray-200 text-gray-700 py-2 px-4 rounded-lg font-medium hover:bg-gray-300"
                >
                  {t('back')}
                </button>
              </div>
            </div>
  );
});

// ========== mp-role: 角色选择 (Player / Viewer) ==========
const MpRoleSection = memo(({
  mp,
  setGameMode, setStake, setJumboMode, setPlayerHandicaps,
  setPlayerNames,
  setSelectedCourse, setPars, setHoles,
  setTotalMoney, setMoneyDetails, setAllScores, setAllUps, setAllPutts,
  setAllWater, setAllOb, setTotalSpent,
  setCurrentHole, setScores, setUps, setPutts, setWater, setOb,
  setCompletedHoles, setGameComplete, setCurrentHoleSettlement,
  setCurrentSection,
  t
}) => {
  // Shared init logic for viewer
  const initGameStateFromRemote = (g) => {
    setGameMode(g.gameMode || 'matchPlay');
    setStake(String(g.stake || ''));
    setJumboMode(g.jumboMode || false);
    setPlayerHandicaps(g.handicaps || {});
    
    const names = [...(g.players || [])];
    while (names.length < (g.jumboMode ? 8 : 4)) names.push('');
    setPlayerNames(names);
    
    if (g.course && g.course.fullName) {
      setSelectedCourse(g.course);
      if (g.course.pars) {
        const newPars = {};
        g.course.pars.forEach((p, i) => { newPars[i + 1] = p; });
        setPars(newPars);
      }
    }

    const allP = g.players || [];
    const initMoney = {};
    const initDetails = {};
    const initAllScores = {};
    const initSpent = {};
    const initAllPutts = {};
    const initAllWater = {};
    const initAllOb = {};
    allP.forEach(player => {
      initMoney[player] = 0;
      initDetails[player] = { fromPool: 0, fromPlayers: {} };
      initAllScores[player] = {};
      initSpent[player] = 0;
      initAllPutts[player] = {};
      initAllWater[player] = {};
      initAllOb[player] = {};
      allP.forEach(other => {
        if (other !== player) initDetails[player].fromPlayers[other] = 0;
      });
    });
    setTotalMoney(initMoney);
    setMoneyDetails(initDetails);
    setAllScores(initAllScores);
    setAllUps({});
    setAllPutts(initAllPutts);
    setAllWater(initAllWater);
    setAllOb(initAllOb);
    setTotalSpent(initSpent);
    setCurrentHole(0);
    setScores({});
    setUps({});
    setPutts({});
    setWater({});
    setOb({});
    setCompletedHoles([]);
    setGameComplete(false);
    setCurrentHoleSettlement(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 py-6">
              <div className="max-w-md mx-auto px-4 space-y-4">
                <div className="text-center">
                  <h2 className="text-xl font-bold text-gray-900">
                    {t('mpRoleTitle')}
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    {t('mpRoleDesc')}
                  </p>
                  <div className="mt-2 inline-flex items-center gap-2 bg-amber-50 text-amber-700 px-3 py-1 rounded-full text-sm font-mono">
                    <Icon name="home" size={16} className="inline-block mr-1 align-text-bottom" />{mp.gameCode}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  {/* Player Card */}
                  <button
                    onClick={() => setCurrentSection('mp-claim')}
                    className="bg-white rounded-xl p-5 shadow-md border-2 border-blue-200 hover:border-blue-400 hover:shadow-lg transition text-left"
                  >
                    <div className="mb-2 flex justify-center"><Icon name="target" size={34} /></div>
                    <div className="text-lg font-bold text-blue-700 mb-1">
                      {t('mpPlayerRole')}
                    </div>
                    <p className="text-xs text-gray-500 mb-3">
                      {t('mpPlayerRoleDesc')}
                    </p>
                    <div className="space-y-1">
                      <div className="text-xs text-blue-600 flex items-center gap-1">
                        <Icon name="edit" size={14} className="inline-block mr-1 align-text-bottom" /><span>{t('mpPlayerTagInput')}</span>
                      </div>
                      <div className="text-xs text-blue-600 flex items-center gap-1">
                        <Icon name="user" size={14} className="inline-block mr-1 align-text-bottom" /><span>{t('mpPlayerTagClaim')}</span>
                      </div>
                    </div>
                  </button>

                  {/* Viewer Card */}
                  <button
                    onClick={async () => {
                      const g = mp.remoteGame;
                      initGameStateFromRemote(g);
                      if (g.holesList) {
                        setHoles(g.holesList);
                      }
                      await mp.joinAsViewer(mp.gameCode);
                      mp.setMultiplayerSection('lobby');
                      setCurrentSection('mp-lobby');
                    }}
                    className="bg-white rounded-xl p-5 shadow-md border-2 border-purple-200 hover:border-purple-400 hover:shadow-lg transition text-left"
                  >
                    <div className="mb-2 flex justify-center"><Icon name="eye" size={34} /></div>
                    <div className="text-lg font-bold text-purple-700 mb-1">
                      {t('mpViewerRole')}
                    </div>
                    <p className="text-xs text-gray-500 mb-3">
                      {t('mpViewerRoleDesc')}
                    </p>
                    <div className="space-y-1">
                      <div className="text-xs text-purple-600 flex items-center gap-1">
                        <Icon name="eye" size={14} className="inline-block mr-1 align-text-bottom" /><span>{t('mpViewerTagLive')}</span>
                      </div>
                      <div className="text-xs text-gray-400 flex items-center gap-1 line-through">
                        <Icon name="edit" size={14} className="inline-block mr-1 align-text-bottom" /><span>{t('mpViewerTagNoInput')}</span>
                      </div>
                    </div>
                  </button>
                </div>

                <button
                  onClick={() => {
                    mp.resetMultiplayer();
                    mp.setMultiplayerSection(null);
                    setCurrentSection('home');
                  }}
                  className="w-full bg-gray-200 text-gray-700 py-2 px-4 rounded-lg font-medium hover:bg-gray-300"
                >
                  {t('back')}
                </button>
              </div>
            </div>
  );
});

// ========== mp-claim: Joiner 认领球员 ==========
const MpClaimSection = memo(({
  mp, showToast,
  setGameMode, setStake, setJumboMode, setPlayerHandicaps,
  setPlayerNames,
  setSelectedCourse, setPars, setHoles,
  setTotalMoney, setMoneyDetails, setAllScores, setAllUps, setAllPutts,
  setAllWater, setAllOb, setTotalSpent,
  setCurrentHole, setScores, setUps, setPutts, setWater, setOb,
  setCompletedHoles, setGameComplete, setCurrentHoleSettlement,
  setCurrentSection,
  t
}) => {
  const [isClaimingPlayers, setIsClaimingPlayers] = React.useState(false);

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-indigo-50 py-6">
              <div className="max-w-md mx-auto px-4 space-y-4">
                <div className="text-center">
                  <h2 className="text-xl font-bold text-gray-900">
                    {t('mpClaimTitle')}
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    {t('mpClaimDesc')}
                  </p>
                </div>

                <div className="bg-white rounded-xl p-4 shadow-md space-y-2">
                  {!mp.remoteGame || !mp.remoteGame.players || mp.remoteGame.players.length === 0 ? (
                    <div className="text-center py-6 text-gray-500">
                      <p>{t('mpNoPlayers')}</p>
                    </div>
                  ) : (
                    mp.remoteGame.players.map(player => {
                      const claimedBy = mp.claimed[player];
                      const creatorDev = mp.remoteGame?.creatorDevice;
                      // Only disable if claimed by another JOINER (not by creator's default claim)
                      const isClaimedByOtherJoiner = claimedBy && claimedBy !== mp.deviceId && claimedBy !== creatorDev;
                      const isDisabled = isClaimedByOtherJoiner || isClaimingPlayers;

                      return (
                        <label
                          key={player}
                          className={`flex items-center gap-3 py-3 px-3 rounded-lg border border-gray-100 transition ${
                            isDisabled
                              ? 'bg-gray-50 cursor-not-allowed opacity-60'
                              : 'hover:bg-gray-50 cursor-pointer'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={mp.claimChecked[player] || false}
                            disabled={isDisabled}
                            onChange={(e) => {
                              if (!isDisabled) {
                                mp.setClaimChecked(prev => ({ ...prev, [player]: e.target.checked }));
                              }
                            }}
                            className="w-5 h-5 rounded text-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                          />
                          <span className={`font-medium text-lg ${isDisabled ? 'text-gray-500' : 'text-gray-900'}`}>
                            {player}
                          </span>
                          {isClaimedByOtherJoiner && (
                            <span className={`ml-auto text-xs ${mp.getDeviceBgClass(claimedBy)} px-2 py-0.5 rounded`}>
                              <Badge {...mp.getDeviceBadge(claimedBy)} size={18} /> {t('mpClaimed')}
                            </span>
                          )}
                        </label>
                      );
                    })
                  )}
                </div>

                <button
                  onClick={async () => {
                    if (isClaimingPlayers) return; // Prevent double-click

                    const selected = Object.entries(mp.claimChecked).filter(([,v]) => v).map(([k]) => k);
                    if (selected.length === 0) {
                      showToast(t('mpSelectOne'), 'error');
                      return;
                    }

                    try {
                      setIsClaimingPlayers(true);

                      // Apply remote game settings locally
                      const g = mp.remoteGame;
                      if (!g) {
                        showToast(t('mpError'), 'error');
                        return;
                      }

                      setGameMode(g.gameMode || 'matchPlay');
                      setStake(String(g.stake || ''));
                      setJumboMode(g.jumboMode || false);
                      setPlayerHandicaps(g.handicaps || {});
                      // Set player names
                      const names = [...(g.players || [])];
                      while (names.length < (g.jumboMode ? 8 : 4)) names.push('');
                      setPlayerNames(names);

                      // Apply course if available
                      if (g.course && g.course.fullName) {
                        setSelectedCourse(g.course);
                        if (g.course.pars) {
                          const newPars = {};
                          g.course.pars.forEach((p, i) => { newPars[i + 1] = p; });
                          setPars(newPars);
                        }
                      }

                      // Sync holes list from remote game
                      if (g.holesList) {
                        setHoles(g.holesList);
                      }

                      const result = await mp.claimPlayers(selected);

                      if (result.ok) {
                        // Initialize game state locally like startGame does
                        const allP = g.players || [];
                        const initMoney = {};
                        const initDetails = {};
                        const initAllScores = {};
                        const initSpent = {};
                        const initAllPutts = {};
                        const initAllWater = {};
                        const initAllOb = {};
                        allP.forEach(player => {
                          initMoney[player] = 0;
                          initDetails[player] = { fromPool: 0, fromPlayers: {} };
                          initAllScores[player] = {};
                          initSpent[player] = 0;
                          initAllPutts[player] = {};
                          initAllWater[player] = {};
                          initAllOb[player] = {};
                          allP.forEach(other => {
                            if (other !== player) initDetails[player].fromPlayers[other] = 0;
                          });
                        });
                        setTotalMoney(initMoney);
                        setMoneyDetails(initDetails);
                        setAllScores(initAllScores);
                        setAllUps({});
                        setAllPutts(initAllPutts);
                        setAllWater(initAllWater);
                        setAllOb(initAllOb);
                        setTotalSpent(initSpent);
                        setCurrentHole(0);
                        setScores({});
                        setUps({});
                        setPutts({});
                        setWater({});
                        setOb({});
                        setCompletedHoles([]);
                        setGameComplete(false);
                        setCurrentHoleSettlement(null);

                        mp.setMultiplayerSection('lobby');
                        setCurrentSection('mp-lobby');
                      } else {
                        // Show error if claim failed
                        showToast(result.error || t('mpError'), 'error');
                      }
                    } catch (error) {
                      console.error('Error claiming players:', error);
                      showToast(t('mpError'), 'error');
                    } finally {
                      setIsClaimingPlayers(false);
                    }
                  }}
                  disabled={isClaimingPlayers}
                  className={`w-full py-3 px-4 rounded-lg font-semibold text-lg transition ${
                    isClaimingPlayers
                      ? 'bg-blue-400 text-white cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  {isClaimingPlayers ? t('mpProcessing') : t('mpConfirmClaim')}
                </button>

                <button
                  onClick={() => {
                    mp.resetMultiplayer();
                    mp.setMultiplayerSection(null);
                    setCurrentSection('home');
                  }}
                  className="w-full bg-gray-200 text-gray-700 py-2 px-4 rounded-lg font-medium hover:bg-gray-300"
                >
                  {t('back')}
                </button>
              </div>
            </div>
  );
});

export { MpLobbySection, MpRoleSection, MpClaimSection };